import AbstractGenerator from '../../misc/generator';

import baseOptions from './defaultOptions';

import EdgeSlab from './objects/edge-slab';
import FloorSlab from './objects/floor-slab';
import Stairs from './objects/stairs';
import BetweenWalls from './objects/between-walls';
import Pedestal from './objects/pedestal';

import Arrow from '../../objects/arrow';
import { HTMLLabel } from '../../objects/html-label';
import Arrow3D from '@scene/objects/arrow3D';
import { unitLabel, unitTransform } from '@services/helper.service';

import Scene from '@scene';
import { defaultUnits } from '@config';

export default class SafetyBarrierObject extends AbstractGenerator {

  static types = {
    EDGE_SLAB: 'edge_slab',
    FLOOR_SLAB: 'floor_slab',
    STAIRS: 'stairs',
    BETWEEN_WALLS: 'between_walls',
    PEDESTAL: 'pedestal'
  };

  constructor(options = {}, defaultOptions = baseOptions) {
    super(options, defaultOptions);
  }

  setOrientation() {}

  init2DObject() {
    this.options.mode = '2D';

    const object = this.createObject();
    object.init();

    this.layer2D.set(object.layer2D);
  }

  init3DObject() {
    this.options.mode = '3D';

    const object = this.createObject();
    object.init();

    this.layer3D.set(object.layer3D);

    this.applyLoadsLabels(object.barrier);
  }

  createObject() {
    this.barrier = null; // For loads labels

    this.options.panel.circleCorrection = 180;

    switch (this.options.type) {
      case SafetyBarrierObject.types.EDGE_SLAB: {
        const edgeSlab = new EdgeSlab(this.options, this.dispatchOptionUpdate.bind(this));

        this.barrier = edgeSlab.barrier;

        return edgeSlab;
      }
      case SafetyBarrierObject.types.FLOOR_SLAB: {
        const floorSlab = new FloorSlab(this.options, this.dispatchOptionUpdate.bind(this));

        this.barrier = floorSlab.barrier;

        return floorSlab;
      }
      case SafetyBarrierObject.types.STAIRS: {
        const stairs = new Stairs(this.options, this.dispatchOptionUpdate.bind(this));

        this.barrier = stairs.barrier;

        return stairs;
      }
      case SafetyBarrierObject.types.BETWEEN_WALLS: {
        const betweenWalls = new BetweenWalls(this.options, this.dispatchOptionUpdate.bind(this));

        this.barrier = betweenWalls.barrier;

        return betweenWalls;
      }
      case SafetyBarrierObject.types.PEDESTAL: {
        const pedestal = new Pedestal(this.options, this.dispatchOptionUpdate.bind(this));

        this.barrier = pedestal.barrier;

        return pedestal;
      }
      default: {
        throw new Error('Something went wrong!');
      }
    }
  }

  applyLoadsLabels(barrier) {
    if (!this.options.object.loads.display) return;

    let arrowSize = 300;
    let arrowThickness = 7;
    if (this.options.barrier.loads.external.active) {
      let reverseExternalDirection = false;
      if ((this.options.barrier.loads.external.value < 0 && this.options.type !== SafetyBarrierObject.types.BETWEEN_WALLS) ||
          (this.options.barrier.loads.external.value >= 0 && this.options.type === SafetyBarrierObject.types.BETWEEN_WALLS))
        reverseExternalDirection = true;
      // because of the bottom empty space, barrier.size.height is smaller than HGC (options.barrier.height) on between walls 
      const externalArrow = new Arrow3D({
        size: arrowSize,
        color: 0x0000ff,
        isSingle: true,
        position: {
          x: -10,
          y: Math.min(1000 - (this.options.barrier.height - barrier.size.height) - barrier.size.height / 2, barrier.size.height / 2),
          z: 0
        },
        direction: reverseExternalDirection ? Arrow.types.direction.Z : Arrow.types.direction.NegativeZ,
        disableDepthTest: true,
        thickness: arrowThickness
      });
      barrier.object.addObject(externalArrow);

      let loadUnit = unitLabel(Scene.units.load);

      let vyValue = unitTransform(this.options.barrier.loads.external.value)
          .from(defaultUnits.LOAD)
          .to(Scene.units.load);

      const externalHtmlElement = new HTMLLabel({
        text: `Vy: ${vyValue} ${loadUnit}`,
        verticalAlign: HTMLLabel.types.verticalAlign.CENTER,
        align: HTMLLabel.types.align.CENTER,
        color: "#0000ff"
      });
      externalHtmlElement.position.set(
        barrier.position.x + (reverseExternalDirection ? -arrowSize - 50 : arrowSize + 50),
        Math.min((barrier.position.y - this.options.barrier.height / 2) + 1000 - (this.options.barrier.height - barrier.size.height) / 2,
                  barrier.position.y + barrier.size.height / 2),
        barrier.position.z + 10
      );
      this.layer3D.htmlLayer.add(externalHtmlElement);
    }

    if (this.options.barrier.loads.internal.active) {
      let reverseInternalDirection = false;
      if ((this.options.barrier.loads.internal.value < 0 && this.options.type !== SafetyBarrierObject.types.BETWEEN_WALLS) ||
          (this.options.barrier.loads.internal.value >= 0 && this.options.type === SafetyBarrierObject.types.BETWEEN_WALLS))
        reverseInternalDirection = true;

      const internalArrow = new Arrow3D({
        size: arrowSize,
        color: 0x00ff00,
        isSingle: true,
        position: {
          x: 10,
          y: barrier.size.height / 2,
          z: 0
        },
        direction: reverseInternalDirection ? Arrow.types.direction.NegativeZ : Arrow.types.direction.Z,
        disableDepthTest: true,
        thickness: arrowThickness
      });
      barrier.object.addObject(internalArrow);

      let loadUnit = unitLabel(Scene.units.load);

      let vyValue = unitTransform(this.options.barrier.loads.internal.value)
          .from(defaultUnits.LOAD)
          .to(Scene.units.load);

      const internalHtmlElement = new HTMLLabel({
        text: `Vy: ${vyValue} ${loadUnit}`,
        verticalAlign: HTMLLabel.types.verticalAlign.CENTER,
        align: HTMLLabel.types.align.CENTER,
        color: "#00ff00"
      });
      internalHtmlElement.position.set(
        barrier.position.x + (reverseInternalDirection ? arrowSize + 50 : -arrowSize - 50),
        barrier.position.y + barrier.size.height / 2,
        barrier.position.z - 10
      );
      this.layer3D.htmlLayer.add(internalHtmlElement);
    }
  }

}



// WEBPACK FOOTER //
// ./src/object-view/generators/safety-barrier/safety-barrier.object.js